import React, { useRef, useState } from 'react';
import { AttachmentIcon, DownloadIcon, PlusIcon } from '../../../../svgs/svgs';
import PdfIcon from '@/icons/files/PdfIcon2';
import TxtIcon from '@/icons/files/TxtIcon';
import DocIcon from '@/icons/files/DocIcon';
import DocxIcon from '@/icons/files/DocxIcon';
import WebpIcon from '@/icons/files/WebpIcon';
import PngIcon from '@/icons/files/PngIcon';
import JpgIcon from '@/icons/files/JpgIcon';
import JpegIcon from '@/icons/files/JpegIcon';
import { getFileExtension } from '../../../../utils/helpers/helpers';
import { Icon } from '@iconify/react/dist/iconify.js';
import useAttachmentUpload from '@/hooks/useAttachmentUpload';
import AttachmentService, {
  Attachment,
  AttachmentParent,
} from '@/services/AttachmentService';
import { useToast } from '@/context/ToastContext';

const isLikelyCorsError = (err: unknown): boolean => {
  const msg =
    (err as any)?.message?.toString().toLowerCase() ?? '';
  return (
    msg.includes('network error') ||
    msg.includes('cors') ||
    (err as any)?.code === 'ERR_NETWORK'
  );
};

const fileIcons: Record<string, () => React.ReactElement> = {
  pdf: PdfIcon,
  txt: TxtIcon,
  doc: DocIcon,
  docx: DocxIcon,
  jpg: JpgIcon,
  jpeg: JpegIcon,
  png: PngIcon,
  webp: WebpIcon,
};

interface Props {
  parent: AttachmentParent | null;
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export default function Attachments({ parent }: Props) {
  const {
    attachments,
    pending,
    enqueue,
    retry,
    remove,
    dismissPending,
  } = useAttachmentUpload(parent, { enabled: !!parent });

  const [isDragging, setIsDragging] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();

  const totalCount = attachments.length + pending.length;
  const disabled = !parent;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) enqueue(files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) enqueue(files);
  };

  const handleClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const markBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleDownload = async (att: Attachment) => {
    if (busyIds.has(att._id)) return;
    markBusy(att._id, true);
    try {
      await AttachmentService.downloadAttachment(att._id, att.original_name);
    } catch (err) {
      console.error('Download failed', err);
      addToast(
        isLikelyCorsError(err)
          ? `Couldn't download "${att.original_name}". File delivery is misconfigured (CloudFront CORS). Please contact support.`
          : `Couldn't download "${att.original_name}". Please try again.`,
        'error',
        5000
      );
    } finally {
      markBusy(att._id, false);
    }
  };

  const handleDelete = async (att: Attachment) => {
    if (busyIds.has(att._id)) return;
    markBusy(att._id, true);
    try {
      await remove(att._id);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Delete failed', err);
      addToast(
        `Couldn't delete "${att.original_name}". Please try again.`,
        'error',
        5000
      );
    } finally {
      markBusy(att._id, false);
    }
  };

  const handleDownloadAll = async () => {
    for (const att of attachments) {
      // Serial to avoid spawning N tabs/downloads simultaneously.
      // eslint-disable-next-line no-await-in-loop
      await handleDownload(att);
    }
  };

  return (
    <>
      <div className="w-full flex items-center gap-4 justify-between">
        <div className="w-full flex items-center gap-1">
          <div className="w-4">
            <AttachmentIcon />
          </div>
          <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
            Attachment
            {totalCount > 0 && <span className="ml-1">({totalCount})</span>}
          </h3>
        </div>
        {attachments.length > 0 && (
          <button
            onClick={handleDownloadAll}
            className="flex items-center gap-1 hover:opacity-70 transition-opacity"
          >
            <div className="w-4">
              <DownloadIcon />
            </div>
            <p className="text-xs whitespace-nowrap">Download all</p>
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      <div
        onClick={handleClick}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={`border-0.5 border-gray-3/20 rounded-md ${totalCount > 0 ? 'p-3' : 'p-5'} mt-2 bg-modal-input transition-all ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer ' +
              (isDragging
                ? 'border-blue-500 bg-blue-50/5'
                : 'hover:bg-modal-input/80')
        }`}
      >
        {totalCount === 0 ? (
          <div className="flex items-center gap-2">
            <div className="w-5">
              <PlusIcon />
            </div>
            <p className="text-sm">
              Click to attach files, docs, or references to this task
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {attachments.map((att) => {
              const ext = getFileExtension(att.original_name);
              const IconComponent = fileIcons[ext];
              const busy = busyIds.has(att._id);
              const isConfirming = confirmDeleteId === att._id;
              return (
                <div
                  key={att._id}
                  onClick={(e) => e.stopPropagation()}
                  className="flex flex-col gap-2 p-3 bg-[#141414] rounded-lg hover:bg-black-7 transition-colors relative overflow-hidden"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {IconComponent && (
                        <div className="flex-shrink-0">
                          <IconComponent />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-xs font-semibold text-text-primary-body truncate"
                          title={att.display_name || att.original_name}
                        >
                          {att.display_name || att.original_name}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <p className="text-[11px] text-text-primary-body">
                            {formatFileSize(att.size)}
                          </p>
                          <span className="font-bold"> • </span>
                          <button
                            onClick={() => handleDownload(att)}
                            disabled={busy}
                            className="flex items-center gap-0.5 hover:underline underline-offset-2 disabled:opacity-50"
                          >
                            <div className="w-3">
                              <DownloadIcon />
                            </div>
                            <span className="text-[11px]">
                              {busy ? 'Working…' : 'Download'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                    {!isConfirming && (
                      <button
                        onClick={() => setConfirmDeleteId(att._id)}
                        disabled={busy}
                        className="p-0.5 hover:bg-red-500/10 rounded-full border-0.5 border-text-primary-body hover:border-red-500/10 transition-colors group disabled:opacity-50"
                      >
                        <Icon
                          icon={'qlementine-icons:close-16'}
                          className="w-3 h-3 text-text-primary-body group-hover:text-error-2"
                        />
                      </button>
                    )}
                  </div>

                  {isConfirming && (
                    <div className="flex items-center gap-2 p-2 bg-[#1a1a1a] rounded border border-error-2/30">
                      <span className="text-[11px] text-error-2 whitespace-nowrap">
                        Remove this attachment?
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={busy}
                          className="text-[11px] text-text-primary-body hover:underline disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDelete(att)}
                          disabled={busy}
                          className="text-[11px] font-semibold text-error-2 hover:underline disabled:opacity-50"
                        >
                          {busy ? 'Removing…' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {pending.map((p) => {
              const ext = getFileExtension(p.name);
              const IconComponent = fileIcons[ext];
              const failed = p.status === 'failed';
              const showProgress =
                p.status === 'uploading' || p.status === 'completing';
              return (
                <div
                  key={p.localId}
                  onClick={(e) => e.stopPropagation()}
                  className={`flex flex-col gap-2 p-3 rounded-lg relative overflow-hidden ${
                    failed ? 'bg-red-500/10' : 'bg-[#141414]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {IconComponent && (
                        <div className="flex-shrink-0">
                          <IconComponent />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-text-primary-body truncate">
                          {p.name}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <p className="text-[11px] text-text-primary-body">
                            {formatFileSize(p.size)}
                          </p>
                          <span className="font-bold"> • </span>
                          {failed ? (
                            <button
                              onClick={() => retry(p.localId)}
                              className="text-[11px] text-error-2 hover:underline"
                            >
                              Retry
                            </button>
                          ) : (
                            <span className="text-[11px] capitalize text-text-primary-body">
                              {p.status === 'queued' ? 'Queued' : p.status}…
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => dismissPending(p.localId)}
                      className="p-0.5 hover:bg-red-500/10 rounded-full border-0.5 border-text-primary-body hover:border-red-500/10 transition-colors group"
                    >
                      <Icon
                        icon={'qlementine-icons:close-16'}
                        className="w-3 h-3 text-text-primary-body group-hover:text-error-2"
                      />
                    </button>
                  </div>

                  {showProgress && (
                    <div className="w-full">
                      <div className="w-full bg-[#2d2d2d] rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all duration-300 bg-blue-500"
                          style={{ width: `${p.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-text-primary-body mt-1">
                        {p.progress}%
                      </p>
                    </div>
                  )}
                  {failed && p.error && (
                    <p className="text-[11px] text-error-2 mt-1">{p.error}</p>
                  )}
                </div>
              );
            })}

            <div
              onClick={handleClick}
              className="flex items-center gap-2 p-2 border border-dashed border-gray-3/30 rounded hover:border-gray-3/50 transition-colors cursor-pointer"
            >
              <div className="w-4">
                <PlusIcon />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
